import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APIKeyManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/user/api-keys`);
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async () => {
    if (!newApiKey.trim()) {
      alert('Please enter an API key');
      return;
    }

    // Basic validation
    if (!newApiKey.startsWith('sk-')) {
      alert('API keys should start with "sk-"');
      return;
    }

    try {
      setTesting(true);
      
      // Test the key first
      const testResponse = await axios.post(`${API}/user/api-keys/test`, {
        api_key: newApiKey,
        provider: selectedProvider
      });

      if (!testResponse.data.valid) {
        alert('Invalid API key: ' + testResponse.data.message);
        return;
      }

      // Add the key
      await axios.post(`${API}/user/api-keys`, {
        provider: selectedProvider,
        api_key: newApiKey
      });

      alert(`${selectedProvider === 'openai' ? 'OpenAI' : 'DeepSeek'} API key added successfully!`);
      setNewApiKey('');
      setShowAddForm(false);
      fetchApiKeys();
    } catch (error) {
      console.error('Error adding API key:', error);
      alert(error.response?.data?.detail || 'Failed to add API key');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!confirm('Are you sure you want to delete this API key?')) {
      return;
    }

    try {
      await axios.delete(`${API}/user/api-keys/${keyId}`);
      alert('API key deleted successfully');
      fetchApiKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key');
    }
  };

  const handleToggleKey = async (keyId, currentStatus) => {
    try {
      await axios.put(`${API}/user/api-keys/${keyId}`, {
        is_active: !currentStatus
      });
      fetchApiKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
      alert('Failed to update API key');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Info Box */}
      <div className="bg-purple-50 p-4 rounded-lg">
        <h3 className="font-semibold text-purple-900 mb-2">Why add an API key?</h3>
        <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
          <li>Enable AI-powered document summarization</li>
          <li>Automatic extraction of key information (dates, amounts, parties)</li>
          <li>Smart document categorization</li>
          <li><strong>OpenAI:</strong> ~$0.00015 per document (GPT-4o-mini)</li>
          <li><strong>DeepSeek:</strong> ~$0.00007 per document (50% cheaper!)</li>
        </ul>
      </div>

      {/* Existing Keys */}
      {apiKeys.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold">Your API Keys</h3>
          {apiKeys.map((key) => (
            <div key={key.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">
                    {key.provider === 'openai' ? 'OpenAI' : key.provider === 'deepseek' ? 'DeepSeek' : key.provider} API Key
                  </span>
                  <Badge variant={key.is_active ? "default" : "outline"}>
                    {key.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Added: {new Date(key.created_at).toLocaleDateString()}
                  {key.last_used && ` • Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                  {key.usage_count > 0 && ` • Used ${key.usage_count} times`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleKey(key.id, key.is_active)}
                >
                  {key.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteKey(key.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Key Form */}
      {!showAddForm && apiKeys.length === 0 && (
        <Button onClick={() => setShowAddForm(true)} className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Add AI API Key (OpenAI or DeepSeek)
        </Button>
      )}

      {showAddForm && (
        <div className="space-y-3 p-4 bg-purple-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Provider
            </label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              disabled={testing}
              className="w-full p-2 border rounded-lg"
            >
              <option value="openai">OpenAI (GPT-4o-mini) - $0.15/1M tokens</option>
              <option value="deepseek">DeepSeek (deepseek-chat) - $0.07/1M tokens (50% cheaper!)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              API Key
            </label>
            <Input
              type="password"
              placeholder="sk-..."
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              disabled={testing}
            />
            <p className="text-xs text-gray-500 mt-1">
              {selectedProvider === 'openai' ? (
                <>Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">OpenAI Platform</a></>
              ) : (
                <>Get your API key from <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">DeepSeek Platform</a></>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleAddKey}
              disabled={testing}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
            >
              {testing ? 'Testing & Adding...' : 'Test & Add Key'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setNewApiKey('');
              }}
              disabled={testing}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {apiKeys.length > 0 && !showAddForm && (
        <Button
          variant="outline"
          onClick={() => setShowAddForm(true)}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Another Key
        </Button>
      )}
    </div>
  );
};

export default APIKeyManagement;
